import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Code, 
  Image, 
  Table, 
  CheckSquare, 
  Quote, 
  FileText, 
  List, 
  ListOrdered,
  AlertTriangle,
  Info,
  CheckCircle,
  X,
  Bookmark,
  Layout,
  FileCheck,
  Clock,
  Lightbulb,
  MessageSquare,
  PenTool,
  Square,
  ArrowRight,
  AlignLeft,
  MousePointer,
  ChevronDown,
  BarChart,
  Images,
  Palette,
  Highlighter,
  XCircle,
  Check,
  BookOpen,
  Link2,
  Grid2x2
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { BlockCategory } from '../../types/BlockCategory';
import { Editor } from '@tiptap/core';

interface BlockSelectorProps {
  editor: any;
  isOpen: boolean;
  onClose: () => void;
  position: { x: number; y: number };
}

type Block = {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  categories: BlockCategory[];
  action?: (editor: any) => void;
  html?: string;
  insert?: {
    type: string;
    attrs: any;
  };
  actionOnInsert?: (editor: any, html: string) => boolean;
};

// Локальное определение типа BlockCategory для устранения ошибки линтера
type BlockCategory = 
  | 'Все'
  | 'Основные'
  | 'Медиа'
  | 'Макеты'
  | 'Выноски'
  | 'Продвинутые'
  | 'Стилизованные'
  | 'Notion';

// Функция для закрытия селектора блоков
const closeBlockSelector = () => {
  // Скрываем селектор блоков, если он открыт
  const selector = document.querySelector('.block-selector');
  if (selector) {
    selector.classList.add('hidden');
  }
};

const insertHtml = (
  editor: Editor | undefined,
  html: string,
  blockId?: string
) => {
  // Проверка наличия редактора
  if (!editor) {
    console.error('Редактор не определен');
    return false;
  }

  try {
    // Специальная обработка для нашей таблицы
    if (blockId === 'simpleTable') {
      console.log("Inserting Tiptap native table");
      
      // Вставляем стандартную таблицу Tiptap
      editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
      
      // Запускаем настройку кнопок с таймаутом для надежности
      setTimeout(() => {
        console.log("Setting up table controls after insertion");
        setupTables();
      }, 100);
      
      return true;
    }

    // --- Старая логика для других HTML блоков --- 
    if (!html || html.trim() === '') {
      console.error('Пустой HTML контент для не-табличного блока');
      return false;
    }

    let cleanHtml = html
      .replace(/\n\s+/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();

    console.log("Inserting generic HTML block...");
    
    const nodeJsonToInsert = {
      type: 'htmlBlock',
      attrs: {
        'data-type': 'html-block'
      },
      content: [
        { type: 'paragraph' }
      ]
    };

    editor.chain().focus().insertContent(nodeJsonToInsert).run();

    setTimeout(() => {
      const view = editor.view;
      const state = view.state;
      const { from } = state.selection;
      let nodePos: number | null = null;

      state.doc.nodesBetween(Math.max(0, from - 10), from, (node, pos) => {
        if (node.type.name === 'htmlBlock' && !view.nodeDOM(pos)?.querySelector('.tiptap-nodeview-content')?.innerHTML) {
          nodePos = pos;
          return false; // Нашли свежевставленный пустой блок
        }
        return true;
      });
      
      if (nodePos === null) {
        console.warn("Could not find inserted placeholder node by position.");
        // Пробуем найти последний блок без содержимого
        const emptyBlocks = Array.from(document.querySelectorAll('div[data-type="html-block"]')).filter(el => {
          const contentEl = el.querySelector('.tiptap-nodeview-content');
          return contentEl && !contentEl.innerHTML.trim();
        });
        if (emptyBlocks.length > 0) {
          const latestBlock = emptyBlocks[emptyBlocks.length - 1] as HTMLElement;
          const contentElement = latestBlock.querySelector('.tiptap-nodeview-content') as HTMLElement;
          if (contentElement) {
            console.log("Setting innerHTML for fallback empty block:", cleanHtml);
            contentElement.innerHTML = cleanHtml;
          } else {
            console.error("Fallback failed: Couldn't find contentDOM in the empty htmlBlock.");
          }
        } else {
          console.error("Fallback failed: Couldn't find any empty htmlBlock elements.");
        }
        return;
      }

      try {
        const domNode = view.nodeDOM(nodePos) as HTMLElement;
        if (domNode) {
          const contentElement = domNode.querySelector('.tiptap-nodeview-content') as HTMLElement;
          if (contentElement) {
            console.log("Setting innerHTML for element at pos", nodePos, ":", cleanHtml);
            contentElement.innerHTML = cleanHtml;
          } else {
            console.error('Could not find contentDOM element within the node view at pos', nodePos);
          }
        } else {
          console.error('Could not find DOM node for htmlBlock at pos', nodePos);
        }
      } catch (domError) {
        console.error('Error accessing/modifying node DOM:', domError);
      }
    }, 50);

    return true; // Указываем, что вставка (асинхронная) была инициирована

  } catch (error) {
    console.error('Критическая ошибка при вставке блока:', error);
    try {
      editor.chain().focus().insertContent({ type: 'paragraph', content: [{ type: 'text', text: 'Ошибка вставки' }] }).run();
    } catch (e) { /* ignore */ }
    return false;
  }
};

export function BlockSelector({ editor, isOpen, onClose, position }: BlockSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<BlockCategory>('Все');
  const selectorRef = useRef<HTMLDivElement>(null);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  
  // Определяем блоки
  const blocks: Block[] = [
    
    {
      id: 'heading1',
      title: 'Заголовок 1',
      description: 'Большой заголовок',
      icon: <span className="text-sm font-bold">H1</span>,
      categories: ['Все', 'Основные'],
      action: (editor) => {
        editor.chain().focus().toggleHeading({ level: 1 }).run();
      }
    },
    {
      id: 'heading2',
      title: 'Заголовок 2',
      description: 'Средний заголовок',
      icon: <span className="text-sm font-bold">H2</span>,
      categories: ['Все', 'Основные'],
      action: (editor) => {
        editor.chain().focus().toggleHeading({ level: 2 }).run();
      }
    },
    {
      id: 'heading3',
      title: 'Заголовок 3',
      description: 'Маленький заголовок',
      icon: <span className="text-sm font-bold">H3</span>,
      categories: ['Все', 'Основные'],
      action: (editor) => {
        editor.chain().focus().toggleHeading({ level: 3 }).run();
      }
    },
    {
      id: 'bulletList',
      title: 'Маркированный список',
      description: 'Список с маркерами',
      icon: <List className="w-4 h-4" />,
      categories: ['Все', 'Основные'],
      action: (editor) => {
        editor.chain().focus().toggleBulletList().run();
      }
    },
    {
      id: 'orderedList',
      title: 'Нумерованный список',
      description: 'Список с нумерацией',
      icon: <ListOrdered className="w-4 h-4" />,
      categories: ['Все', 'Основные'],
      action: (editor) => {
        editor.chain().focus().toggleOrderedList().run();
      }
    },
    {
      id: 'taskList',
      title: 'Список задач',
      description: 'Список с чекбоксами',
      icon: <CheckSquare className="w-4 h-4" />,
      categories: ['Все', 'Основные'],
      action: (editor) => {
        editor.chain().focus().toggleTaskList().run();
      }
    },
    {
      id: 'codeBlock',
      title: 'Блок кода',
      description: 'Блок для форматированного кода',
      icon: <Code className="w-4 h-4" />,
      categories: ['Все', 'Продвинутые'],
      action: (editor) => {
        editor.chain().focus().toggleCodeBlock().run();
      }
    },
    {
      id: 'divider',
      title: 'Разделитель',
      description: 'Горизонтальная линия',
      icon: <span className="w-4 h-0.5 bg-gray-400 rounded-full"></span>,
      categories: ['Все', 'Основные'],
      action: (editor) => {
        editor.chain().focus().setHorizontalRule().run();
        onClose();
      }
    },
    {
      id: 'simpleTable',
      title: 'Таблица',
      description: 'Стандартная таблица Tiptap с контролами',
      icon: <Table className="w-4 h-4 text-blue-500" />,
      categories: ['Все', 'Основные', 'Макеты'],
      // Убираем html, так как вставляем через команду
      // html: `...` 
      // Добавляем action, который вызовет insertHtml со специальным ID
       action: (editor) => insertHtml(editor, '', 'simpleTable')
    }
  ];

  // Закрытие селектора при клике вне его
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Фильтрация блоков по поиску и категории
  const filteredBlocks = blocks.filter(block => {
    const matchesSearch = block.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          block.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'Все' || block.categories.includes(activeCategory);
    return matchesSearch && matchesCategory;
  });

  // Вычисляем позицию окна в центре экрана
  useEffect(() => {
    if (isOpen) {
      // Центрируем модальное окно
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Предполагаемые размеры модального окна
      const modalWidth = 500; // примерная ширина
      const modalHeight = 600; // примерная высота
      
      setModalPosition({
        top: Math.max(20, (viewportHeight - modalHeight) / 2),
        left: Math.max(20, (viewportWidth - modalWidth) / 2)
      });
    }
  }, [isOpen]);

  // Функция для вставки блока HTML
  const insertBlock = (blockHtml: string, blockId?: string) => {
    insertHtml(editor, blockHtml, blockId);
    setTimeout(() => {
      onClose();
    }, 0);
  };

  // Функция для вставки встроенного блока TipTap
  const insertBuiltInBlock = (action: (editor: any) => void) => {
    action(editor);
    setTimeout(() => {
    onClose();
    }, 0);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div 
        ref={selectorRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-3xl overflow-hidden"
        style={{ 
          maxHeight: '70vh'
        }}
      >
        <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-base font-medium text-gray-900 dark:text-white">Вставить блок</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск блоков..."
              className="w-full p-2 pl-8 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
            />
            <div className="absolute left-2.5 top-1/2 transform -translate-y-1/2">
              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          <div className="flex p-1.5">
            {(['Все', 'Основные', 'Медиа', 'Макеты', 'Выноски', 'Продвинутые', 'Стилизованные', 'Notion'] as BlockCategory[]).map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-2.5 py-0.5 text-xs rounded-md mr-1 whitespace-nowrap ${
                  activeCategory === category
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
        
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(70vh - 140px)' }}>
          <div className="grid grid-cols-3 gap-2 p-3">
            {filteredBlocks.map((block) => (
              <button
                key={block.id}
                onClick={() => {
                  // Проверяем, есть ли HTML-контент в блоке
                  if (typeof block.action === 'function') {
                    // Для стандартных блоков TipTap используем прямой вызов action
                    insertBuiltInBlock(block.action);
                  } else if (block.insert) {
                    // Для специальных типов блоков (например, канбан-доски)
                    editor.chain().focus().insertContent(block.insert).run();
                    onClose();
                  } else {
                    // Для HTML-блоков используем insertBlock, который использует insertStyledHtmlBlock
                    insertBlock(block.html || '', block.id);
                  }
                }}
                className="flex flex-col items-start p-2 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className="flex items-center w-full mb-1">
                  <div className="flex-shrink-0 text-gray-500 dark:text-gray-400 mr-2">
                    {block.icon}
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">{block.title}</span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">{block.description}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Переносим определение setupTables в конец файла, чтобы оно было доступно
document.addEventListener('DOMContentLoaded', function() {
  // Вызов здесь может быть преждевременным
});

// HTML строка для тулбара - значительно упрощенная и с меньшим количеством классов
const tableToolbarHtml = `
<div class="table-controls-wrapper" style="position: absolute; top: 5px; right: 5px; z-index: 9999 !important; display: flex; gap: 3px;">
  <!-- Add Row -->
  <button type="button" class="table-btn add-row" style="width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: #f8f9fa; border: 1px solid #ddd; cursor: pointer;">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 5v14M5 12h14"></path>
    </svg>
  </button>
  <!-- Add Column -->
  <button type="button" class="table-btn add-column" style="width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: #f8f9fa; border: 1px solid #ddd; cursor: pointer;">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 5v14M5 12h14" transform="rotate(90 12 12)"></path>
    </svg>
  </button>
  <!-- Delete Table -->
  <button type="button" class="table-btn delete-table" style="width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: #fee2e2; border: 1px solid #fecaca; cursor: pointer; color: #ef4444;">
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <path d="M18 6L6 18M6 6l12 12"></path>
    </svg>
  </button>
</div>
`;

// Стили для показа/скрытия кнопок при наведении на таблицу
const tableControlsStyles = `
<style id="table-controls-styles">
/* По умолчанию кнопки скрыты */
.tableWrapper .table-controls-wrapper {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
  pointer-events: all !important; /* Разрешаем клики по кнопкам */
}

/* При наведении на .tableWrapper показываем кнопки */
.tableWrapper:hover .table-controls-wrapper {
  opacity: 1 !important;
  visibility: visible !important;
}

/* Стили для кнопок при наведении */
.table-btn:hover {
  filter: brightness(0.95);
  transform: scale(1.05);
}

/* !!! НОВОЕ ПРАВИЛО: Скрываем плейсхолдер внутри ячеек таблицы !!! */
.tiptap table th > p.is-empty::before,
.tiptap table td > p.is-empty::before {
  content: "" !important; /* Убираем текст плейсхолдера */
  display: none !important; /* Скрываем псевдоэлемент */
}

/* Особые стили для темного режима */
@media (prefers-color-scheme: dark) {
  .table-btn {
    background: #374151 !important;
    border-color: #4b5563 !important;
    color: #e5e7eb !important;
  }
  .table-btn.delete-table {
    background: rgba(185, 28, 28, 0.2) !important;
    border-color: rgba(185, 28, 28, 0.3) !important;
    color: #f87171 !important;
  }
}
</style>
`;

// Глобальная функция-обработчик для всех кнопок таблицы (делегирование)
function handleTableControlEvents(event: Event) {
    // Проверяем, что это событие мыши
    if (!(event instanceof MouseEvent)) {
        return;
    }
    
    const target = event.target as Element;
    const button = target.closest('.table-btn') as HTMLButtonElement | null;
    if (!button) return;
    
    // Находим ближайшую обертку таблицы
    const wrapper = button.closest('.tableWrapper') as HTMLElement | null;
    if (!wrapper) return;
    
    // Находим таблицу внутри обертки
    const table = wrapper.querySelector('table') as HTMLTableElement | null;
    if (!table) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    console.log("Table button clicked:", button.className);
    
    // --- Добавить строку --- 
    if (button.classList.contains('add-row')) {
        const tbody = table.tBodies[0];
        if (!tbody) return;
        const cellCount = table.rows[0]?.cells.length || 3;
        const newRow = tbody.insertRow();
        for (let i = 0; i < cellCount; i++) {
            const cell = newRow.insertCell();
            // Добавляем <p> внутрь, как это делает Tiptap
            const p = document.createElement('p');
            cell.appendChild(p);
            cell.className = 'border dark:border-gray-700 p-2';
            p.textContent = 'Ячейка';
        }
        console.log("Row added successfully");
    }
    // --- Добавить столбец --- 
    else if (button.classList.contains('add-column')) {
        const rows = Array.from(table.rows);
        rows.forEach((row, rowIndex) => {
            if (rowIndex === 0 && table.tHead) {
                const th = document.createElement('th');
                th.className = 'border-b-2 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 font-bold p-2 text-left';
                const p = document.createElement('p');
                p.textContent = 'Заголовок';
                th.appendChild(p);
                row.appendChild(th);
            } else {
                const td = row.insertCell();
                td.className = 'border dark:border-gray-700 p-2';
                const p = document.createElement('p');
                p.textContent = 'Ячейка';
                td.appendChild(p);
            }
        });
        console.log("Column added successfully");
    }
    // --- Удалить таблицу --- 
    else if (button.classList.contains('delete-table')) {
        if (wrapper.parentNode) {
            wrapper.parentNode.removeChild(wrapper);
            console.log("Table removed successfully");
        }
    }
}

// Добавляем глобальные стили один раз при загрузке скрипта
function addGlobalTableStyles() {
    // Добавляем стили один раз
    if (!document.getElementById('table-controls-styles')) {
        document.head.insertAdjacentHTML('beforeend', tableControlsStyles);
        console.log("Global table control styles added to head");
    }
}

// Обновленная функция setupTables
function setupTables() {
    // Сначала добавляем глобальные стили
    addGlobalTableStyles();
    
    console.log("Setting up tables and looking for .tableWrapper elements without controls");
    
    // Ищем все таблицы, которые еще не имеют наших контролов
    const tableWrappers = document.querySelectorAll('.tableWrapper:not([data-has-controls="true"])');
    
    console.log(`Found ${tableWrappers.length} table wrappers without controls`);
    
    tableWrappers.forEach((wrapper, index) => {
        console.log(`Adding controls to table wrapper ${index}`);
        
        // Добавляем тулбар кнопок в начало wrapper
        wrapper.insertAdjacentHTML('afterbegin', tableToolbarHtml);
        
        // Отмечаем, что эта таблица уже имеет контролы
        wrapper.setAttribute('data-has-controls', 'true');
        console.log(`Controls added to table wrapper ${index}`);
    });
    
    // Добавляем один делегированный обработчик событий на уровне документа
    // (если он еще не был добавлен)
    if (!window.tableControlsInitialized) {
        document.addEventListener('click', handleTableControlEvents as EventListener);
        window.tableControlsInitialized = true;
        console.log("Global click handler for table controls attached to document");
    }
}

// Добавляем глобальную переменную для отслеживания инициализации
declare global {
    interface Window {
        tableControlsInitialized?: boolean;
    }
}

// Инициализация таблиц по загрузке DOM
document.addEventListener('DOMContentLoaded', () => {
    console.log("DOM loaded - initializing table controls");
    setupTables();
    
    // Устанавливаем MutationObserver для поиска новых таблиц
    const observer = new MutationObserver((mutations) => {
        let tableAdded = false;
        
        mutations.forEach(mutation => {
            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach(node => {
                    if (node instanceof Element) {
                        // Проверяем, добавлена ли таблица
                        if (node.matches('.tableWrapper') || node.querySelectorAll('.tableWrapper').length > 0) {
                            tableAdded = true;
                        }
                    }
                });
            }
        });
        
        if (tableAdded) {
            console.log("New table detected - setting up controls");
            setupTables();
        }
    });
    
    // Наблюдаем за изменениями в редакторе
    const editor = document.querySelector('.tiptap');
    if (editor) {
        observer.observe(editor, { childList: true, subtree: true });
        console.log("MutationObserver started for .tiptap element");
    }
});

